/**
 * useSplitCartPayment Hook
 * Sepet bölme (split cart) ödemesi — SplitCartModal'dan çağrılır.
 * usePayment hook'undaki fonksiyonları (nakit/POS/parçalı) birebir kullanır;
 * yazarkasa, fiş yazdırma, mutfak ekranı vs. tüm özellikler dahildir.
 *
 * İçerik:
 *  - handleSplitCartPayment: ödeme akışını başlatan ve Promise<boolean> döndüren handler
 *  - companion useEffect: state güncellemeleri tamamlandıktan sonra ilgili usePayment
 *    fonksiyonunu çağıran yürütme yarısı
 *
 * Not: splitPaymentResolveRef ve newCartAfterPaymentRef QuickSaleScreen'de tanımlı kalır
 * (handleClosePartialPaymentModal ve render'da da kullanıldıkları için) ve buraya param
 * olarak geçilir. Split-payment state'leri de usePayment yapılandırmasında kullanıldığı
 * için ekranda kalır ve param/setter olarak alınır.
 */

import { useCallback, useEffect } from 'react';
import { AlertColor } from '@mui/material';
import type { CartItem } from '../types/product.types';

export interface UseSplitCartPaymentParams {
  // Ekranda tanımlı, dışarıda da paylaşılan ref'ler
  splitPaymentResolveRef: React.MutableRefObject<((value: boolean) => void) | null>;
  newCartAfterPaymentRef: React.MutableRefObject<CartItem[] | null>;
  // Sepet + snackbar
  setCart: (cart: CartItem[]) => void;
  showSnackbar: (message: string, severity?: AlertColor) => void;
  posOptions: any[];
  // Split-payment state (ekranda kalır)
  pendingSplitPayment: any;
  setPendingSplitPayment: React.Dispatch<React.SetStateAction<any>>;
  isSplitPaymentMode: boolean;
  setIsSplitPaymentMode: React.Dispatch<React.SetStateAction<boolean>>;
  splitPaymentSuccessCallback: (() => void) | undefined;
  setSplitPaymentSuccessCallback: React.Dispatch<React.SetStateAction<(() => void) | undefined>>;
  pendingSplitCartItems: CartItem[];
  setPendingSplitCartItems: React.Dispatch<React.SetStateAction<CartItem[]>>;
  setPendingSplitActiveSaleId: React.Dispatch<React.SetStateAction<number | null>>;
  setSplitCartPartialPaymentInfo: React.Dispatch<React.SetStateAction<any>>;
  setIsPartialPaymentModalOpen: (open: boolean) => void;
  // usePayment fonksiyonları
  hookHandleCashPayment: () => Promise<any>;
  hookHandlePosPayment: (
    bankAccountId: number,
    subAccountId: number | null,
    label: string,
    useFiscalDevice: boolean
  ) => Promise<any>;
  hookProcessPaymentAfterPartial: (details: any) => any;
}

export interface UseSplitCartPaymentReturn {
  handleSplitCartPayment: (
    cartType: 'left' | 'right',
    paymentType: 'cash' | 'pos' | 'partial',
    cartItems: CartItem[],
    total: number,
    invoiceNumber: string,
    newCartAfterPayment?: CartItem[],
    restaurantActiveSaleId?: number | null
  ) => Promise<boolean>;
}

/**
 * Sepet bölme ödemesi hook'u
 */
export const useSplitCartPayment = ({
  splitPaymentResolveRef,
  newCartAfterPaymentRef,
  setCart,
  showSnackbar,
  posOptions,
  pendingSplitPayment,
  setPendingSplitPayment,
  isSplitPaymentMode,
  setIsSplitPaymentMode,
  splitPaymentSuccessCallback,
  setSplitPaymentSuccessCallback,
  pendingSplitCartItems,
  setPendingSplitCartItems,
  setPendingSplitActiveSaleId,
  setSplitCartPartialPaymentInfo,
  setIsPartialPaymentModalOpen,
  hookHandleCashPayment,
  hookHandlePosPayment,
  hookProcessPaymentAfterPartial,
}: UseSplitCartPaymentParams): UseSplitCartPaymentReturn => {
  const handleSplitCartPayment = useCallback(async (
    cartType: 'left' | 'right',
    paymentType: 'cash' | 'pos' | 'partial',
    cartItems: CartItem[],
    total: number,
    invoiceNumber: string,
    newCartAfterPayment?: CartItem[], // ✅ YENİ: Ödeme sonrası ana cart'a atanacak sepet
    restaurantActiveSaleId?: number | null // ✅ YENİ: Restoran modunda kapatılacak ActiveSale Id
  ): Promise<boolean> => {
    console.log(`💰 [SPLIT PAYMENT] ${cartType} sepet ${paymentType} ödeme başlatılıyor...`, {
      cartItems: cartItems.length,
      total,
      invoiceNumber,
      newCartAfterPayment: newCartAfterPayment?.length || 0,
      restaurantActiveSaleId
    });

    // ✅ RESTORAN SEPET BÖLME FIX: hook'un completeSale çağrısında hangi satışı
    // kapatacağını belirlemesi için override'ı state'e al. Bunu hook'a
    // `restaurantActiveSaleIdOverride` olarak geçiriyoruz.
    setPendingSplitActiveSaleId(restaurantActiveSaleId ?? null);

    // Temel kontroller
    if (cartItems.length === 0) {
      showSnackbar('Sepet boş!', 'warning');
      return false;
    }

    // ✅ Parçalı ödeme için özel işlem
    if (paymentType === 'partial') {
      console.log('💰 [SPLIT PAYMENT] Parçalı ödeme modalı açılıyor...', {
        cartType,
        cartItems: cartItems.length,
        total,
        invoiceNumber
      });

      // Parçalı ödeme bilgisini state'e kaydet
      setSplitCartPartialPaymentInfo({
        cartType,
        cartItems,
        total,
        invoiceNumber,
        newCartAfterPayment: newCartAfterPayment || []
      });

      // Parçalı ödeme modalını aç
      setIsPartialPaymentModalOpen(true);

      // Promise döndür - parçalı ödeme tamamlandığında resolve edilecek
      return true; // Parçalı ödeme modalı açıldı
    }

    // ✅ YENİ: Ödeme sonrası kullanılacak yeni cart'ı ref'e kaydet
    newCartAfterPaymentRef.current = newCartAfterPayment || null;
    console.log(`📦 [SPLIT PAYMENT] newCartAfterPaymentRef set edildi: ${newCartAfterPayment?.length || 0} ürün`);

    return new Promise((resolve) => {
      // Resolve fonksiyonunu ref'e kaydet (useEffect'ten erişilebilir olması için)
      splitPaymentResolveRef.current = resolve;

      // Ödeme sonrası callback'i ayarla - clearCart yerine bu çağrılacak
      const successCallback = () => {
        console.log(`✅ [SPLIT PAYMENT] ${cartType} sepet ödemesi başarılı, callback çağrıldı`);

        // ✅ YENİ: Ödeme sonrası yeni cart'ı ref'ten al ve set et
        const pendingNewCart = newCartAfterPaymentRef.current;
        if (pendingNewCart !== null) {
          console.log(`📦 [SPLIT PAYMENT] Yeni cart set ediliyor: ${pendingNewCart.length} ürün`);
          setCart([...pendingNewCart]); // Yeni cart'ı hemen set et
          newCartAfterPaymentRef.current = null; // Ref'i temizle
        }

        // ✅ YENİ: Pending split cart'ı temizle
        setPendingSplitCartItems([]);
        // ✅ YENİ: Restoran sepet bölme override'ını temizle
        setPendingSplitActiveSaleId(null);

        // Sepet bölme modunu kapat
        setIsSplitPaymentMode(false);
        setSplitPaymentSuccessCallback(undefined);
        setPendingSplitPayment(null);
        // Başarılı sonucu döndür
        if (splitPaymentResolveRef.current) {
          splitPaymentResolveRef.current(true);
          splitPaymentResolveRef.current = null;
        }
      };

      // Sepet bölme modunu aktif et ve callback'i set et
      setIsSplitPaymentMode(true);
      setSplitPaymentSuccessCallback(() => successCallback);

      // ✅ YENİ: Ana cart'ı değiştirmeden, ödenecek sepeti ayrı state'e kaydet
      // Bu sayede ana cart korunur ve successCallback'te doğru şekilde güncellenebilir
      console.log(`💰 [SPLIT PAYMENT] Split cart items ayarlanıyor (${cartItems.length} ürün)...`);
      setPendingSplitCartItems([...cartItems]);

      // POS için varsayılan seçeneği bul
      let posOption = null;
      if (paymentType === 'pos') {
        posOption = posOptions.find(p => p.bankAccountId);
        if (!posOption) {
          showSnackbar('POS ayarı bulunamadı!', 'error');
          setIsSplitPaymentMode(false);
          setSplitPaymentSuccessCallback(undefined);
          setPendingSplitActiveSaleId(null); // ✅ Override temizle
          resolve(false);
          return;
        }
      }

      // Pending ödeme bilgisini set et - useEffect bu değişikliği izleyecek
      setPendingSplitPayment({
        paymentType: paymentType as 'cash' | 'pos',
        posOption
      });
    });
  }, [showSnackbar, posOptions]);

  // ✅ YENİ: Sepet bölme ödemesi için useEffect - state güncellemeleri tamamlandıktan sonra ödeme yap
  useEffect(() => {
    if (pendingSplitPayment && isSplitPaymentMode && splitPaymentSuccessCallback && pendingSplitCartItems.length > 0) {
      console.log('🔄 [SPLIT PAYMENT] State güncellemeleri tamamlandı, ödeme başlatılıyor...');
      console.log(`📦 [SPLIT PAYMENT] pendingSplitCartItems: ${pendingSplitCartItems.length} ürün`);

      const executePayment = async () => {
        try {
          if (pendingSplitPayment.paymentType === 'cash') {
            console.log('💵 [SPLIT PAYMENT] Nakit ödeme fonksiyonu çağrılıyor...');
            await hookHandleCashPayment();
          } else if (pendingSplitPayment.paymentType === 'pos' && pendingSplitPayment.posOption) {
            console.log('💳 [SPLIT PAYMENT] POS ödeme fonksiyonu çağrılıyor...');
            await hookHandlePosPayment(
              parseInt(String(pendingSplitPayment.posOption.bankAccountId)) || 0,
              pendingSplitPayment.posOption.subAccountId ? parseInt(String(pendingSplitPayment.posOption.subAccountId)) : null,
              pendingSplitPayment.posOption.label || 'POS',
              true // useFiscalDevice
            );
          } else if (pendingSplitPayment.paymentType === 'partial' && pendingSplitPayment.partialPaymentDetails) {
            console.log('📝 [SPLIT PAYMENT] Parçalı ödeme fonksiyonu çağrılıyor...');
            hookProcessPaymentAfterPartial(pendingSplitPayment.partialPaymentDetails);
          }
        } catch (error: any) {
          console.error('❌ [SPLIT PAYMENT] Ödeme hatası:', error);
          showSnackbar(error.message || 'Ödeme sırasında bir hata oluştu!', 'error');
          setIsSplitPaymentMode(false);
          setSplitPaymentSuccessCallback(undefined);
          setPendingSplitPayment(null);
          setPendingSplitCartItems([]); // ✅ YENİ: Hata durumunda da temizle
          setPendingSplitActiveSaleId(null); // ✅ YENİ: Hata durumunda override temizle
          if (splitPaymentResolveRef.current) {
            splitPaymentResolveRef.current(false);
            splitPaymentResolveRef.current = null;
          }
        }
      };

      // Pending'i temizle ve ödemeyi başlat
      setPendingSplitPayment(null);
      executePayment();
    }
  }, [pendingSplitPayment, isSplitPaymentMode, splitPaymentSuccessCallback, pendingSplitCartItems, hookHandleCashPayment, hookHandlePosPayment, hookProcessPaymentAfterPartial, showSnackbar]);

  return { handleSplitCartPayment };
};

export default useSplitCartPayment;
